import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../shared/contexts/AuthContext';
import ChoshiiLogo from '../ChoshiiLogo'; // ✅ adjust path if needed
import adminApi from '../../shared/api/AxiosAdmin';
import { setAdminAccessToken } from '../../shared/api/AxiosAdmin';

const AdminHome: React.FC = () => {
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await adminApi.post('/admin/auth/logout', {}); // or '/auth/logout' if shared
    } catch {}
  
    localStorage.removeItem('unyAdminToken');
    setAdminAccessToken(null);
  
    navigate('/admin/login', { replace: true });
  };
  
  

  const ActionButton = ({
    title,
    desc,
    onClick,
    tone = 'neutral',
  }: {
    title: string;
    desc: string;
    onClick: () => void;
    tone?: 'neutral' | 'primary' | 'success';
  }) => {
    const stylesByTone: Record<string, React.CSSProperties> = {
      neutral: {
        background: '#020617',
        border: '1px solid #4b5563',
        color: '#e5e7eb',
      },
      primary: {
        background: '#b1b8c7',
        border: '1px solid rgba(177,184,199,0.35)',
        color: '#020617',
      },
      success: {
        background: 'linear-gradient(135deg,#22c55e,#a3e635)',
        border: '1px solid rgba(163,230,53,0.35)',
        color: '#020617',
      },
    };

    const arrowBg =
      tone === 'primary' || tone === 'success' ? 'rgba(2,6,23,0.08)' : 'rgba(255,255,255,0.06)';
    const arrowBorder =
      tone === 'primary' || tone === 'success' ? 'rgba(2,6,23,0.12)' : 'rgba(255,255,255,0.10)';

    return (
      <button
        type="button"
        onClick={onClick}
        style={{
          width: '100%',
          textAlign: 'left',
          borderRadius: 16,
          padding: '14px 14px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 14,
          transition: 'transform 140ms ease, box-shadow 140ms ease, filter 140ms ease',
          boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
          ...stylesByTone[tone],
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-2px)';
          (e.currentTarget as HTMLButtonElement).style.filter = 'brightness(1.03)';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0px)';
          (e.currentTarget as HTMLButtonElement).style.filter = 'brightness(1)';
        }}
      >
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: -0.02 }}>
            {title}
          </div>
          <div
            style={{
              marginTop: 4,
              fontSize: 13,
              opacity: tone === 'primary' || tone === 'success' ? 0.85 : 0.75,
              color: tone === 'primary' || tone === 'success' ? '#020617' : '#b1b8c7',
            }}
          >
            {desc}
          </div>
        </div>

        <span
          style={{
            flex: '0 0 auto',
            width: 38,
            height: 38,
            borderRadius: 12,
            display: 'grid',
            placeItems: 'center',
            background: arrowBg,
            border: `1px solid ${arrowBorder}`,
            color: tone === 'primary' || tone === 'success' ? '#020617' : '#e5e7eb',
            fontSize: 18,
            marginTop: 1,
          }}
        >
          →
        </span>
      </button>
    );
  };

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        minHeight: '100vh',
        background: 'radial-gradient(circle at top, #020617 0, #000 40%, #020617 100%)',
        color: '#f9fafb',
        fontFamily: 'system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Top bar (same vibe as StartPage/AdminLogin) */}
      <header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 30,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 25px',
          background: 'rgba(2, 6, 23, 0.9)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(148,163,184,0.2)',
        }}
      >
        <div
          style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => navigate('/')}
          title="Back to home"
        >
          <ChoshiiLogo
            animateSheen
            fontFamily="'Playfair Display', serif"
            style={{ width: 160 }}
            padding={0}
          />
        </div>

        <button
          type="button"
          onClick={handleLogout}
          style={{
            borderRadius: 999,
            padding: '6px 14px',
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 600,
            color: '#fed7aa',
            background: 'rgba(127,29,29,0.25)',
            border: '1px solid rgba(248,113,113,0.5)',
          }}
        >
          Logout
        </button>
      </header>

      {/* Centered dashboard window */}
      <main
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: 760,
            borderRadius: 24,
            border: '1px solid rgba(148,163,184,0.3)',
            background: 'linear-gradient(145deg, rgba(15,23,42,0.98), rgba(2,6,23,0.98))',
            boxShadow: '0 25px 60px rgba(0,0,0,0.9)',
            padding: '20px 20px 24px',
            position: 'relative',
          }}
        >
          {/* back arrow inside the card */}
          <button
            type="button"
            onClick={() => navigate('/')}
            aria-label="Back"
            style={{
              position: 'absolute',
              top: 16,
              left: 16,
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg
              width="25"
              height="25"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#b1b8c7"
              strokeWidth="2.0"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>

          <div style={{ textAlign: 'center', marginBottom: 18, marginTop: 12 }}>
            <h1
              style={{
                fontSize: 24,
                marginBottom: 6,
                letterSpacing: -0.03,
                color: '#b1b8c7',
              }}
            >
              Admin Dashboard
            </h1>
            <p style={{ fontSize: 13, color: '#b1b8c7' }}>
              Choose what you want to manage.
            </p>
          </div>

          {/* actions grid */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              gap: 12,
            }}
          >
            <ActionButton
              title="Ads Billing Overview"
              desc="Revenue, invoices, and billing status."
              tone="primary"
              onClick={() => navigate('/admin/billing')}
            />

            <ActionButton
              title="Review Pending Ads"
              desc="Approve or reject submitted ad campaigns."
              tone="neutral"
              onClick={() => navigate('/admin/ads-review')}
            />

            <ActionButton
              title="Manage Music Library"
              desc="Upload and edit tracks, covers, and metadata."
              tone="neutral"
              onClick={() => navigate('/upload-music')}
            />

<ActionButton
  title="Media Moderation"
  desc="Browse uploads, view media, and delete posts."
  tone="neutral"
  onClick={() => navigate('/admin/media')}
 />

 <ActionButton
  title="Post Reports"
  desc="Review posts reported by users and take action."
  tone="neutral"
  onClick={() => navigate('/admin/post-reports')}
/>

            <div
              style={{
                borderRadius: 16,
                padding: 14,
                border: '1px dashed rgba(148,163,184,0.35)',
                background: 'rgba(255,255,255,0.04)',
                color: '#b1b8c7',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                minHeight: 86,
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 700, color: '#e5e7eb' }}>
                Tip
              </div>
              <div style={{ marginTop: 4, fontSize: 13, opacity: 0.85 }}>
                Add more admin tools here later (users, reports, settings).
              </div>
            </div>
          </div>

          <div
            style={{
              marginTop: 14,
              height: 1,
              width: '100%',
              background: 'linear-gradient(to right, transparent, #1f2937, transparent)',
            }}
          />

          <p style={{ marginTop: 12, textAlign: 'center', fontSize: 11, color: '#6b7280' }}>
            © {new Date().getFullYear()} • Admin access only
          </p>

          {/* responsive: stack on small screens */}
          <style>{`
            @media (max-width: 640px) {
              .admin-grid {
                grid-template-columns: 1fr !important;
              }
            }
          `}</style>
        </div>
      </main>
    </div>
  );
};

export default AdminHome;
